// Public, no-login page reached by scanning the QR code on an inventory container.
// It lists what should be in that container — the same sheet the Inventory page
// prints, built from the same module (kitList.js) and downloadable as the same PDF.
//
// It is rendered directly by index.js — OUTSIDE the auth gate, MSAL and the app
// providers — so it never triggers a sign-in redirect, and it fetches with plain
// `fetch` rather than the app's axios (whose interceptors would attach a bearer token
// nobody here has, and raise toasts into a page with no Notifier).
//
// The token in the URL is the only credential. Everything that makes that safe is
// server-side: 128 bits of entropy looked up in the database, and a payload narrowed
// to what the printed sheet shows. See backend core/kit_qr.py.
import { useEffect, useState } from "react";
import {
  Alert, AlertTitle, Box, Button, CircularProgress, Divider, Paper, Stack, Typography,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { API_BASE_URL } from "./../config";
import { kitListSheet, orderedSections } from "./../kitList";
import { exportContentsPdf } from "./../exportContentsPdf";

const LOGO_URL = process.env.PUBLIC_URL + "/ShelterStoneLogo.png";

// The path is /kit/<token>, so the token is the last segment. A path rather than a
// query string keeps the URL — and so the printed code — as short as possible.
const tokenFromUrl = () =>
  decodeURIComponent(window.location.pathname.replace(/\/+$/, "").split("/").pop() || "");

// How far to indent a section for its nesting. The container's own loose items (depth 0)
// and its top-level pockets (depth 1) both sit flush; a pouch inside a pocket steps in
// from there, capped so a deep nest can't walk off a narrow screen.
const indent = (depth) => Math.min(Math.max(depth - 1, 0), 2) * 2;

export default function KitList() {
  const token = tokenFromUrl();
  // loading | ready | notfound | error
  const [phase, setPhase] = useState("loading");
  const [sheet, setSheet] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token || token === "kit") {
      setPhase("notfound");
      return;
    }
    let live = true;
    fetch(`${API_BASE_URL}/kit-list/${encodeURIComponent(token)}`)
      .then(res => {
        if (res.status === 404) return null;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (!live) return;
        if (!data) { setPhase("notfound"); return; }
        setSheet(kitListSheet(data));
        setPhase("ready");
      })
      .catch(() => live && setPhase("error"));
    return () => { live = false; };
  }, [token]);

  // Keep these links out of search results. robots.txt disallows /kit/ as well — that
  // is the reliable half; this covers a crawler that fetched the page anyway.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  useEffect(() => {
    if (sheet?.name) document.title = `${sheet.name} — contents list`;
  }, [sheet]);

  const savePdf = () => {
    setSaving(true);
    exportContentsPdf(sheet).finally(() => setSaving(false));
  };

  return (
    <Box sx={{ maxWidth: 700, mx: "auto", p: 2 }}>
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <img src={LOGO_URL} alt="Shelter Stone" style={{ maxWidth: 260, width: "100%" }} />
      </Box>

      {phase === "loading" && (
        <Stack sx={{ alignItems: "center", py: 6 }} spacing={2}>
          <CircularProgress />
          <Typography color="text.secondary">Loading contents list…</Typography>
        </Stack>
      )}

      {phase === "notfound" && (
        <Alert severity="warning">
          <AlertTitle>Not linked to a container</AlertTitle>
          This code doesn't match anything in the inventory. It may have been printed for
          a container that has since been removed — ask whoever looks after the kit.
        </Alert>
      )}

      {phase === "error" && (
        <Alert severity="error">
          <AlertTitle>Couldn't load the contents list</AlertTitle>
          Check your signal and try again.
        </Alert>
      )}

      {phase === "ready" && sheet && (
        <>
          {/* The printed sheet's own header, so the screen and the paper read alike:
              red title, then the container's name in a black badge. */}
          <Typography variant="h5" sx={{ color: "error.main", fontWeight: 700 }}>
            CONTENTS LIST
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Required contents
          </Typography>
          <Box sx={{ bgcolor: "common.black", color: "common.white", px: 1, py: 0.5, mt: 1,
                     display: "inline-block" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {sheet.name.toUpperCase()}
            </Typography>
          </Box>

          <Box sx={{ mt: 2, mb: 3 }}>
            <Button variant="outlined" startIcon={<PictureAsPdfIcon />}
                    disabled={saving} onClick={savePdf}>
              Download PDF
            </Button>
          </Box>

          {sheet.sections.length === 0 ? (
            <Typography color="text.secondary">
              Nothing is recorded as belonging in this container yet.
            </Typography>
          ) : orderedSections(sheet.sections).map((section, i) => (
            // One column, in the printed sheet's own reading order: loose items, then
            // lid, top, middle, bottom, left to right within each band. The PDF puts
            // the pockets of a band side by side; three columns don't fit a phone, so
            // here the same order simply runs down the screen.
            //
            // Nesting is shown by indent instead — a pouch inside a pocket is indented
            // under it, and `orderedSections` has already kept it directly after its
            // parent. Kept small, and only from `sm` up, so a narrow screen kicks
            // nothing off the right-hand edge.
            <Paper key={`${section.title}-${i}`} variant="outlined"
                   sx={{ mb: 1.5, ml: { xs: 0, sm: indent(section.depth) } }}>
              <Box sx={{ bgcolor: "success.dark", color: "common.white", px: 1.5, py: 0.75 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {section.title}
                </Typography>
                {section.subtitle && (
                  <Typography variant="caption" sx={{ opacity: 0.85 }}>
                    {section.subtitle}
                  </Typography>
                )}
              </Box>
              <Box sx={{ px: 1.5, py: 1 }}>
                {section.items.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    Nothing recorded in here
                  </Typography>
                ) : section.items.map((item, k) => (
                  <Typography key={`${item.name}-${k}`} variant="body2" sx={{ py: 0.25 }}>
                    •&nbsp;&nbsp;{item.name}
                    {item.required == null ? "" : ` ×${item.required}`}
                  </Typography>
                ))}
              </Box>
            </Paper>
          ))}

          {/* The container's own notes close the list, as they close the printed sheet. */}
          {(sheet.notes || "").trim() && (
            <Paper variant="outlined" sx={{ mt: 2 }}>
              <Box sx={{ bgcolor: "success.dark", color: "common.white", px: 1.5, py: 0.75 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Notes</Typography>
              </Box>
              <Typography variant="body2" sx={{ px: 1.5, py: 1, whiteSpace: "pre-wrap" }}>
                {sheet.notes}
              </Typography>
            </Paper>
          )}

          <Divider sx={{ my: 3 }} />
          <Typography variant="caption" color="text.secondary">
            This is the list of what should be in this container, not a record of what it
            currently holds.
          </Typography>
        </>
      )}
    </Box>
  );
}
